"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { Camera as CameraPro, CameraType } from "react-camera-pro";
import { Button } from "@/components/ui/button";
import { track } from "@vercel/analytics";
import { Camera, Maximize2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { reportError } from "@/lib/report-error";
import {
  requestFullscreen,
  subscribeFullscreenChange,
  isFullscreenApiEnabled,
  isFullscreenActive as getFullscreenActive,
} from "@/lib/fullscreen";

export { requestFullscreen, exitFullscreen } from "@/lib/fullscreen";

export interface CameraCaptureProps {
  /** Called when a photo is captured, with the image as a data URL */
  onCapture: (imageDataUrl: string) => void;
  /** Whether to show camera controls (defaults to true) */
  showControls?: boolean;
  /** Additional class names for the container */
  className?: string;
  /** Whether the camera is in landscape mode for positioning adjustments */
  isLandscape?: boolean;
  /** Custom capture button render prop */
  renderCaptureButton?: (props: { onClick: () => void; disabled: boolean }) => React.ReactNode;
}

export function CameraCapture({
  onCapture,
  showControls = true,
  className,
  isLandscape = false,
  renderCaptureButton,
}: CameraCaptureProps) {
  const cameraRef = useRef<CameraType>(null);
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");
  const [numberOfCameras, setNumberOfCameras] = useState(0);
  const [cameraError, setCameraError] = useState<string | null>(null);
  /** Tracks fullscreen so we can show "Enter fullscreen" after camera permission (retry needs a new gesture). */
  const [fullscreenActive, setFullscreenActive] = useState(false);

  const prevLandscapeRef = useRef(isLandscape);

  useEffect(() => {
    track("camera_opened");
  }, []);

  useEffect(() => {
    if (isLandscape && !prevLandscapeRef.current) {
      track("camera_rotated_landscape");
    }
    prevLandscapeRef.current = isLandscape;
  }, [isLandscape]);

  useEffect(() => {
    if (typeof document === "undefined" || !isFullscreenApiEnabled()) {
      return;
    }
    setFullscreenActive(getFullscreenActive());
    return subscribeFullscreenChange(setFullscreenActive);
  }, []);

  const switchCamera = useCallback(() => {
    if (cameraRef.current) {
      const result = cameraRef.current.switchCamera();
      if (result) {
        setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
      }
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!cameraRef.current) return;

    try {
      const imageSrc = cameraRef.current.takePhoto();
      if (typeof imageSrc === "string" && imageSrc.startsWith("data:image")) {
        onCapture(imageSrc);
      } else {
        setCameraError("Failed to capture photo. Please try again.");
      }
    } catch (err) {
      reportError(err, { component: "CameraCapture", action: "capturePhoto" });
      setCameraError("Failed to capture photo. Please try again.");
    }
  }, [onCapture]);

  const retryCamera = useCallback(() => {
    setCameraError(null);
  }, []);

  const showEnterFullscreenHint =
    numberOfCameras > 0 &&
    isFullscreenApiEnabled() &&
    !fullscreenActive;

  if (cameraError) {
    return (
      <div className={cn("flex min-h-0 flex-1 flex-col items-center justify-center bg-black", className)}>
        <div className="flex flex-col items-center gap-5 p-8 text-center text-white">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/10">
            <Camera className="size-10 opacity-70" strokeWidth={1.5} />
          </div>
          <p className="text-base">{cameraError}</p>
          <Button
            variant="secondary"
            size="lg"
            className="h-12 px-8 text-base"
            onClick={retryCamera}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative flex min-h-0 flex-1 flex-col items-center justify-center bg-black overflow-hidden", className)}>
      {showEnterFullscreenHint && (
        <div className="absolute inset-x-0 top-safe-offset-4 z-20 flex justify-center px-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="pointer-events-auto shadow-lg"
            onClick={() => void requestFullscreen()}
          >
            <Maximize2 className="mr-2 size-4 shrink-0" strokeWidth={1.5} />
            Enter fullscreen
          </Button>
        </div>
      )}
      <CameraPro
        ref={cameraRef}
        facingMode={facingMode}
        numberOfCamerasCallback={setNumberOfCameras}
        errorMessages={{
          noCameraAccessible: "No camera accessible. Please connect a camera or grant permission.",
          permissionDenied: "Camera permission denied. Please allow camera access in your browser settings.",
          switchCamera: "Cannot switch camera. Only one camera is available.",
          canvas: "Canvas is not supported in this browser.",
        }}
        aspectRatio="cover"
      />

      {showControls && (
        <>
          {/* Camera switch button */}
          {numberOfCameras > 1 && (
            <Button
              variant="secondary"
              size="icon"
              className={cn(
                "absolute z-10 h-12 w-12 rounded-full bg-black/50 text-white hover:bg-black/70",
                isLandscape
                  ? "left-safe-offset-4 top-safe-offset-4"
                  : "left-safe-offset-4 top-safe-offset-4"
              )}
              onClick={switchCamera}
            >
              <RefreshCw className="size-5" strokeWidth={1.5} />
              <span className="sr-only">Switch Camera</span>
            </Button>
          )}

          {/* Capture button */}
          {renderCaptureButton ? (
            renderCaptureButton({ onClick: capturePhoto, disabled: false })
          ) : (
            <div
              className={cn(
                isLandscape
                  ? "fixed right-safe-offset-4 top-1/2 z-10 -translate-y-1/2"
                  : "absolute inset-x-0 bottom-0 flex justify-center pb-safe-or-8"
              )}
            >
              <Button
                size="lg"
                className={cn(
                  "rounded-full shadow-lg active:scale-95",
                  isLandscape ? "h-16 w-16" : "h-20 w-20"
                )}
                onClick={capturePhoto}
              >
                <Camera
                  className={cn("shrink-0", isLandscape ? "size-8" : "size-10")}
                  strokeWidth={1.5}
                />
                <span className="sr-only">Take Photo</span>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
